import { useState, useEffect, useCallback, useMemo } from 'react'
import MapView from './components/MapView'
import Sidebar from './components/Sidebar'
import RegionPopup from './components/RegionPopup'
import LoadingScreen from './components/LoadingScreen'
import Legend from './components/Legend'
import ConstructionConfirmModal from './components/ConstructionConfirmModal'
import { fetchRegionStats, OWN_LAYER_KEYS, deriveStructOptions, buildLoginUrl, buildLogoutUrl } from './utils/regionStats'
import { fetchWeather } from './utils/weather'
import { fetchCurrentMonth } from './utils/season'
import {
  fetchCountryStructures, fetchStructureTypeData, constructStructure,
  moveStructure, constructInfrastructure, fetchAllInfrastructure, fetchInfraTypes,
  fetchMyProfile, updateInfrastructurePath, pathLengthKm,
  pathSegments, editorPathSegments,
  fetchHousingOptions, housingCost, housingComfort,
} from './utils/structures'
import TopBar from './components/TopBar'
import UnitPanel from './components/UnitPanel'
import ToastStack from './components/Toast'
import ActiveToolStatus from './components/ActiveToolStatus'
import CommandPalette from './components/CommandPalette'
import SearchModal from './components/SearchModal'
import ShortcutHints from './components/ShortcutHints'
import { OVERLAY_STYLE_KEY, DEFAULT_OVERLAY_STYLES, defaultInfraStyle } from './utils/overlayStyles'
import { useMapStore, mapState } from './store/mapStore'
import { registerToastHandler } from './api/client'
import { assembleState, buildShareUrl, decodeState, SHARE_PARAM } from './utils/mapSharing'
import MultiSelectPanel from './components/MultiSelectPanel'
import WeatherTimeline from './components/WeatherTimeline'
import SettingsPanel from './components/SettingsPanel'
import { usePrefsStore } from './store/prefsStore'
import {
  fetchMilitaryDeployments, fetchOrbat, deployUnit, unitCategory,
  fetchPlans, fetchPlan, fetchSharedPlan, createPlan, updatePlan,
  deletePlan as apiDeletePlan, sharePlan, fetchSymbols,
} from './utils/military'
import './styles/App.css'

// All mutually-exclusive map layer keys (base + owned-country heatmaps).
const ALL_LAYER_KEYS = [
  'countries', 'population', 'populationDensity', 'gdpPerCapita', 'climate', 'weather', 'hemisphere', 'playedCountries',
  'topography', 'organizations',
  ...OWN_LAYER_KEYS,
]

// Remembers the "annoncer dans le salon secret" choice across sessions.
const NOTIFY_SECRET_KEY = 'pr-map-notify-secret'

const buildLayerState = (activeKey) =>
  Object.fromEntries(ALL_LAYER_KEYS.map((k) => [k, k === activeKey]))

function App() {
  const [mapLoaded, setMapLoaded] = useState(false)
  const [selectedRegion, setSelectedRegion] = useState(null)
  const [mapLayers, setMapLayers] = useState(() => buildLayerState(usePrefsStore.getState().defaultLayer || 'countries'))

  // Owned-country per-region stats (only the logged-in player's country)
  const [regionStats, setRegionStats] = useState(null) // { region_id: {...} }
  const [ownedCountryId, setOwnedCountryId] = useState(null)
  const [statMeta, setStatMeta] = useState({ structure_types: [], infra_types: [] })
  const [statType, setStatType] = useState({ structures: null, infrastructure: null })
  const [structOptions, setStructOptions] = useState({ types: [], specialisations: [], levels: [] })
  // Structure heatmap filter: '' / null means "all" for that dimension.
  const [structFilter, setStructFilter] = useState({ type: '', specialisation: '', level: '' })
  const [filters, setFilters] = useState({
    hemisphere: null, // 'north', 'south', null
    continent: null,
    minPopulation: 0,
    maxPopulation: 10000000
  })
  const [activeCountriesCount, setActiveCountriesCount] = useState(0)
  const [regionsData, setRegionsData] = useState(null) // Shared regions data from MapView
  const [weatherData, setWeatherData] = useState(null) // { region_id: { weather_type, ... } }
  // Weather timeline (S9): when a past month is selected, this overrides the
  // live weather everywhere. null = live.
  const [timelineWeather, setTimelineWeather] = useState(null)
  const [currentMonth, setCurrentMonth] = useState(null) // RP month (1-12), for season display

  // Plan Mode state
  const [planMode, setPlanMode] = useState(false)
  const [customColors, setCustomColors] = useState({}) // { region_id: color }
  const [selectedColor, setSelectedColor] = useState('#FF5733')
  const [selectionMode, setSelectionMode] = useState('single') // 'single', 'area'
  const [centerToRegionHandler, setCenterToRegionHandler] = useState(null)

  // Structures overlay + Construction Mode state
  const [showStructures, setShowStructures] = useState(false)
  const [showInfrastructure, setShowInfrastructure] = useState(false)

  // Real-world reference overlays (cities / roads / railways). Static
  // Natural-Earth data, lazy-loaded inside MapView the first time each is
  // enabled — see scripts/preprocess_overlays.py for how the raw multi-MB
  // files are simplified into public/data/overlays/.
  const [showCities, setShowCities] = useState(false)
  const [showRoads, setShowRoads] = useState(false)
  const [showRailways, setShowRailways] = useState(false)

  // Bubble map (S10): proportional circles by a chosen metric, an alternative
  // to the choropleth fill.
  const [showBubbles, setShowBubbles] = useState(false)
  const [bubbleMetric, setBubbleMetric] = useState('population')
  const [bubbleColor, setBubbleColor] = useState(() => usePrefsStore.getState().bubbleColor)
  const [bubbleSize, setBubbleSize] = useState(() => usePrefsStore.getState().bubbleSize)
  useEffect(() => {
    usePrefsStore.getState().setPrefs({ bubbleColor, bubbleSize })
  }, [bubbleColor, bubbleSize])

  // Customizable colour/width for the line + point overlays (world roads,
  // railways, cities, and RP infrastructure per type). Persisted; resettable
  // per overlay. See utils/overlayStyles.js for defaults and the width-ramp model.
  const [overlayStyles, setOverlayStyles] = useState(() => {
    try {
      const raw = localStorage.getItem(OVERLAY_STYLE_KEY)
      if (raw) {
        const saved = JSON.parse(raw)
        return {
          ...DEFAULT_OVERLAY_STYLES,
          ...saved,
          infrastructure: { ...DEFAULT_OVERLAY_STYLES.infrastructure, ...(saved.infrastructure || {}) },
        }
      }
    } catch { /* ignore malformed */ }
    return DEFAULT_OVERLAY_STYLES
  })
  useEffect(() => {
    localStorage.setItem(OVERLAY_STYLE_KEY, JSON.stringify(overlayStyles))
  }, [overlayStyles])

  // Update one field of a simple overlay (roads/railways/cities).
  const updateOverlayStyle = useCallback((overlay, field, value) => {
    setOverlayStyles((prev) => ({ ...prev, [overlay]: { ...prev[overlay], [field]: value } }))
  }, [])
  const resetOverlayStyle = useCallback((overlay) => {
    setOverlayStyles((prev) => ({ ...prev, [overlay]: { ...DEFAULT_OVERLAY_STYLES[overlay] } }))
  }, [])

  // Update / reset one RP infrastructure type's style.
  const updateInfraStyle = useCallback((type, field, value) => {
    setOverlayStyles((prev) => {
      const cur = prev.infrastructure[type] || defaultInfraStyle(type)
      return { ...prev, infrastructure: { ...prev.infrastructure, [type]: { ...cur, [field]: value } } }
    })
  }, [])
  const resetInfraStyle = useCallback((type) => {
    setOverlayStyles((prev) => {
      const next = { ...prev.infrastructure }
      delete next[type]
      return { ...prev, infrastructure: next }
    })
  }, [])
  // Military units overlay (?layer=military pre-enables it; ?milplan=TOKEN
  // loads a shared plan; ?warplan=1 opens la planification militaire depuis
  // le dashboard; ?deploy=ID arrive du bouton "Déployable sur la carte")
  const [showMilitary, setShowMilitary] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('layer') === 'military' || params.has('milplan')
      || params.has('warplan') || params.has('deploy')
  })
  // Planification militaire (cartes d'état-major): opened by its own sidebar
  // button — the plans UI in the panel only shows while this is on.
  const [milPlanningOpen, setMilPlanningOpen] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.has('milplan') || params.has('warplan')
  })
  // Unit to highlight in the deploy list (?deploy=ID from the dashboard)
  const [deployHighlightId] = useState(() => {
    const id = new URLSearchParams(window.location.search).get('deploy')
    return id ? Number(id) : null
  })
  const [militaryUnits, setMilitaryUnits] = useState([])
  const [selectedUnit, setSelectedUnit] = useState(null)
  const [milRefresh, setMilRefresh] = useState(0) // bump to refetch deployments
  // Customizable category layers (avions / hélicos / terrestre / FS / soutien…)
  const [milCategories, setMilCategories] = useState({})
  const [myOrbatUnits, setMyOrbatUnits] = useState([]) // flat own units (deploy panel)
  // Military plans (standalone files, shareable read-only)
  const [milPlans, setMilPlans] = useState([])
  const [activePlan, setActivePlan] = useState(null)
  const [readOnlyPlan, setReadOnlyPlan] = useState(false)
  const [planTool, setPlanTool] = useState(null)
  const [planDraft, setPlanDraft] = useState([]) // in-progress line/polygon/arrow points
  const [planDirty, setPlanDirty] = useState(false)
  // War planification: multicalque — each feature belongs to a named calque
  const [planLayer, setPlanLayer] = useState('Calque 1')          // active calque
  const [hiddenPlanLayers, setHiddenPlanLayers] = useState([])    // hidden calque names
  const [planColor, setPlanColor] = useState('#e07070')           // draw color
  // Draw style (diagrams.net-like): line width, dash pattern, arrow heads
  const [planStyle, setPlanStyle] = useState({ width: 'normal', dash: 'solid', heads: 1 })
  // Free symbol placement: selected SIDC (from saved symbols or typed)
  const [planSymbol, setPlanSymbol] = useState(null)
  const [mySymbols, setMySymbols] = useState([])
  const [myProfile, setMyProfile] = useState(null) // { country_id, country_name, player_name, balance }
  const [structuresData, setStructuresData] = useState([]) // flat list from GET /structures
  const [structureTypeData, setStructureTypeData] = useState([]) // buildable catalogue from GET /structures/data
  const [constructionMode, setConstructionMode] = useState(false)
  // Active tool inside Construction Mode: 'structure' (place/move buildings)
  // or 'infra' (draw infrastructure paths). One mode, two tools.
  const [constructionTool, setConstructionTool] = useState('structure')
  // Housing is not levelled: a Logement is a density/style/quality/capacity
  // combination (mirrors MARC /construct_housing), the others a spec + level.
  const [constructionSelection, setConstructionSelection] = useState({
    type: '', specialisation: '', level: null,
    density_id: null, style_id: null, quality_id: null, capacity: 1000,
  })
  const [housingOptions, setHousingOptions] = useState(null) // {densities, styles, qualities}
  const [constructionStatus, setConstructionStatus] = useState({ state: 'idle', message: '' }) // idle|pending|success|error
  const [skipConstructionConfirm, setSkipConstructionConfirm] = useState(false)
  // Announce builds in the country's secret channel rather than its public one.
  // Persisted so players don't have to re-tick it every session.
  const [notifySecret, setNotifySecret] = useState(() => {
    try { return localStorage.getItem(NOTIFY_SECRET_KEY) === '1' } catch { return false }
  })
  const [pendingConstruction, setPendingConstruction] = useState(null) // { region, lngLat, cost }
  // Infrastructure drawing (the 'infra' tool)
  const [infrastructurePath, setInfrastructurePath] = useState([]) // [{ region, lngLat }]
  const [infrastructureSelection, setInfrastructureSelection] = useState({ type: '' })
  const [infraTypes, setInfraTypes] = useState([]) // catalogue {type, cost_per_km}
  const [infrastructureData, setInfrastructureData] = useState([]) // built infra with geometry
  // Rework mode: editing an existing infra's path. Budget = its paid
  // length_km — the redrawn path may not exceed it (server re-enforces).
  const [editingInfra, setEditingInfra] = useState(null) // { id, type, owned_km }

  // Derived tool activity — the window flags and MapView props are built
  // from these so map gestures stay unambiguous (a click is either a
  // structure placement or an infra waypoint, never both).
  const structureToolActive = constructionMode && constructionTool === 'structure'
  const infraToolActive = constructionMode && constructionTool === 'infra'
  
  // Map projection state
  const [isGlobeView, setIsGlobeView] = useState(() => usePrefsStore.getState().defaultProjection === 'globe')
  const [satelliteMode, setSatelliteMode] = useState('none') // 'none' | 'satellite'

  // Persist the last-used projection + active layer as the defaults for next
  // visit (S13 "default projection / preferred active layers").
  useEffect(() => {
    usePrefsStore.getState().setPref('defaultProjection', isGlobeView ? 'globe' : 'mercator')
  }, [isGlobeView])
  useEffect(() => {
    const active = ALL_LAYER_KEYS.find((k) => mapLayers[k])
    if (active) usePrefsStore.getState().setPref('defaultLayer', active)
  }, [mapLayers])

  // Border rendering: none / per-region outlines / per-country outlines
  // (the latter computed client-side — see MapView's borderMode effect),
  // plus thickness and color once a mode is active.
  const [borderMode, setBorderMode] = useState('region') // 'none' | 'region' | 'country'
  const [borderWidth, setBorderWidth] = useState(() => usePrefsStore.getState().borderWidth)
  const [borderColor, setBorderColor] = useState(() => usePrefsStore.getState().borderColor)
  const [borderOpacity, setBorderOpacity] = useState(() => usePrefsStore.getState().borderOpacity) // 0..1
  const [borderStyle, setBorderStyle] = useState(() => usePrefsStore.getState().borderStyle) // solid|dashed|dotted|mixed
  const [haloEnabled, setHaloEnabled] = useState(() => usePrefsStore.getState().haloEnabled)
  const [haloColor, setHaloColor] = useState(() => usePrefsStore.getState().haloColor)
  // Persist border look so it survives reloads (S13 preferences).
  useEffect(() => {
    usePrefsStore.getState().setPrefs({ borderWidth, borderColor, borderOpacity, borderStyle, haloEnabled, haloColor })
  }, [borderWidth, borderColor, borderOpacity, borderStyle, haloEnabled, haloColor])

  // Ruler: click-to-place-points distance measurement, same interaction as
  // infrastructure path drawing but with no ownership/cost/server call.
  const [rulerMode, setRulerMode] = useState(false)
  const [rulerPath, setRulerPath] = useState([]) // [{ lngLat: {lng, lat} }]
  const [rulerCrowFlies, setRulerCrowFlies] = useState(false) // total: along drawn path vs straight line
  // "Focused" point (last placed or dragged) for each tool — arrow keys nudge it.
  const [selectedRulerPoint, setSelectedRulerPoint] = useState(null)
  const [selectedInfraPoint, setSelectedInfraPoint] = useState(null)

  // Organizations map layer: one selected org highlighted, members colored,
  // everyone else neutral. Catalog + membership come baked into the GeoJSON
  // (see generate_geojson_fast.py) — organizationsCatalog is derived once
  // regionsData loads.
  const [selectedOrgId, setSelectedOrgId] = useState(null)
  const organizationsCatalog = useMemo(() => regionsData?.organizations || {}, [regionsData])

  // Toast notifications (replaces alert() for non-blocking feedback)
  const [toasts, setToasts] = useState([])
  const showToast = useCallback((message, type = 'info') => {
    const id = `${Date.now()}-${Math.random()}`
    setToasts((prev) => [...prev, { id, message, type }])
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000)
  }, [])
  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  // Pre-resolve area_* keys to flat regionId map (avoids expensive expandAreaKeys in MapView)
  const customColorsFlat = useMemo(() => {
    if (!regionsData || Object.keys(customColors).length === 0) return {}
    
    const flat = {}
    const areaIndex = regionsData._areaIndex
    
    Object.entries(customColors).forEach(([key, color]) => {
      if (key.startsWith('area_')) {
        // Resolve area to all region IDs using pre-built index
        const areaName = key.replace('area_', '')
        const regionIds = areaIndex?.get(areaName) || []
        regionIds.forEach(regionId => {
          flat[regionId] = color
        })
      } else {
        // Direct region ID
        flat[key] = color
      }
    })
    
    return flat
  }, [customColors, regionsData])

  // Sync tool state into the shared store (formerly window.__* globals). The
  // MapView click handler is registered once and captures the first render's
  // callbacks, so anything those callbacks need at click time must live in the
  // store (read fresh via mapState()), not in a stale closure — the catalogue
  // is fetched async and would otherwise stay [].
  useEffect(() => {
    useMapStore.setState({
      planModeActive: planMode,
      selectedColor,
      selectionMode,
      constructionModeActive: structureToolActive,
      infrastructureModeActive: infraToolActive,
      rulerModeActive: rulerMode,
      ownedCountryId,
      constructionSelection,
      skipConstructionConfirm,
      notifySecret,
      infrastructurePath,
      structureTypeData,
      housingOptions,
      infrastructureData,
    })
  }, [planMode, selectedColor, selectionMode, structureToolActive, infraToolActive, rulerMode, ownedCountryId, constructionSelection, skipConstructionConfirm, notifySecret, infrastructurePath, structureTypeData, housingOptions, infrastructureData])

  // Route API-layer failures (from non-React utils) through the toast stack.
  useEffect(() => { registerToastHandler(showToast) }, [showToast])

  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false)
  const [searchModalOpen, setSearchModalOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)

  // Light / dark theme (pr-design-system reads [data-theme] on the root).
  const theme = usePrefsStore((s) => s.theme)
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme || 'dark')
    // Inline color-scheme wins over any stylesheet, so native form controls
    // (radios/checkboxes/selects) actually follow our theme.
    document.documentElement.style.colorScheme = theme === 'light' ? 'light' : 'dark'
  }, [theme])
  const toggleTheme = useCallback(() => {
    const p = usePrefsStore.getState()
    p.setPref('theme', p.theme === 'light' ? 'dark' : 'light')
  }, [])
  // Ocean colour follows the theme (whitish in light mode).
  const mapBackground = theme === 'light' ? '#dce6f0' : '#1a1a1a'

  // Hidden actions-menu sections (Désactiver éléments).
  const hiddenSections = usePrefsStore((s) => s.hiddenSections)
  const toggleSection = useCallback((id) => {
    const p = usePrefsStore.getState()
    const set = new Set(p.hiddenSections || [])
    set.has(id) ? set.delete(id) : set.add(id)
    p.setPref('hiddenSections', [...set])
  }, [])

  // Global keyboard shortcuts: Ctrl/Cmd+K opens the command palette, Ctrl/Cmd+F
  // the search modal, Esc
  // cancels whichever draw mode is active, Ctrl/Cmd+Z undoes the last point
  // of whichever tool is active (ruler > infra > war-plan draft, in that
  // priority), arrow keys nudge the last-touched ruler/infra point (see
  // handleRulerClick/handleInfraPointMove — "touched" = placed or dragged,
  // no separate click-to-select gesture), and ZQSD/WASD pan the map (via a
  // window event MapView listens for — same pattern as PNG export).
  useEffect(() => {
    const PAN_KEYS = {
      z: [0, -1], w: [0, -1],
      s: [0, 1],
      q: [-1, 0], a: [-1, 0],
      d: [1, 0],
    }
    // Held pan keys — the union vector is pushed to MapView on every change
    // and it pans continuously (rAF) while the vector is non-zero. Holding
    // Z+D therefore glides diagonally instead of stuttering on key repeat.
    const held = new Set()
    const pushPanVector = () => {
      let dx = 0, dy = 0
      held.forEach((k) => { const v = PAN_KEYS[k]; if (v) { dx += v[0]; dy += v[1] } })
      window.dispatchEvent(new CustomEvent('map-pan-vector', {
        detail: { dx: Math.sign(dx), dy: Math.sign(dy) },
      }))
    }
    const stopPan = () => { if (held.size) { held.clear(); pushPanVector() } }

    const handleKeyUp = (e) => {
      const k = e.key.toLowerCase()
      if (held.has(k)) { held.delete(k); pushPanVector() }
    }

    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setCommandPaletteOpen((v) => !v)
        setSearchModalOpen(false)
        return
      }
      // Ctrl/Cmd+F — override the browser's native find with our region search.
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
        e.preventDefault()
        setSearchModalOpen((v) => !v)
        setCommandPaletteOpen(false)
        return
      }
      if (commandPaletteOpen) {
        if (e.key === 'Escape') setCommandPaletteOpen(false)
        return // arrow-nav/Enter inside the palette are handled by CommandPalette itself
      }
      if (searchModalOpen) {
        if (e.key === 'Escape') setSearchModalOpen(false)
        return // handled inside SearchModal
      }

      const tag = document.activeElement?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return

      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        const k = e.key.toLowerCase()
        if (PAN_KEYS[k]) {
          e.preventDefault()
          if (!e.repeat && !held.has(k)) { held.add(k); pushPanVector() }
          return
        }
      }

      if (e.key === 'Escape') {
        if (rulerMode) { handleToggleRulerMode(); return }
        if (constructionMode) { handleToggleConstructionMode(); return }
        if (planMode) { handleTogglePlanMode(); return }
        if (activePlan && planTool) { setPlanTool(null); return }
        return
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        if (rulerMode && rulerPath.length > 0) { e.preventDefault(); handleUndoRulerPoint(); return }
        if (infraToolActive && infrastructurePath.length > 0) { e.preventDefault(); handleUndoInfraPoint(); return }
        if (activePlan && !readOnlyPlan && (planDraft.length || activePlan.features?.length)) {
          e.preventDefault()
          if (planDraft.length) { setPlanDraft((d) => d.slice(0, -1)); return }
          setActivePlan((p) => p && { ...p, features: p.features.slice(0, -1) })
          setPlanDirty(true)
          return
        }
        return
      }

      if (e.key.startsWith('Arrow') && (selectedRulerPoint != null || selectedInfraPoint != null)) {
        const step = e.shiftKey ? 0.001 : 0.02
        const deltas = { ArrowUp: [0, step], ArrowDown: [0, -step], ArrowLeft: [-step, 0], ArrowRight: [step, 0] }
        const delta = deltas[e.key]
        if (!delta) return
        e.preventDefault()
        const [dLng, dLat] = delta
        if (selectedRulerPoint != null) {
          setRulerPath((prev) => prev.map((p, i) => (
            i === selectedRulerPoint ? { lngLat: { lng: p.lngLat.lng + dLng, lat: p.lngLat.lat + dLat } } : p
          )))
        } else if (selectedInfraPoint != null) {
          setInfrastructurePath((prev) => prev.map((p, i) => (
            i === selectedInfraPoint ? { ...p, lngLat: { lng: p.lngLat.lng + dLng, lat: p.lngLat.lat + dLat } } : p
          )))
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    window.addEventListener('blur', stopPan)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
      window.removeEventListener('blur', stopPan)
      stopPan()
    }
  }, [
    rulerMode, rulerPath, constructionMode, planMode, activePlan, planTool, readOnlyPlan, planDraft,
    infraToolActive, infrastructurePath, selectedRulerPoint, selectedInfraPoint, commandPaletteOpen, searchModalOpen,
  ])

  // Fetch the logged-in player's per-region stats (scoped server-side to their
  // own country). Uses the shared SSO cookie; silently no-ops when logged out.
  useEffect(() => {
    let cancelled = false
    fetchRegionStats()
      .then((data) => {
        if (cancelled || !data) return
        setOwnedCountryId(data.country_id ?? null)
        setRegionStats(data.regions || {})
        setStatMeta({
          structure_types: data.structure_types || [],
          infra_types: data.infra_types || [],
        })
        setStatType({
          structures: (data.structure_types || [])[0] || null,
          infrastructure: (data.infra_types || [])[0] || null,
        })
        setStructOptions(deriveStructOptions(data.regions || {}))
      })
      .catch(() => { /* logged out / network — map stays neutral */ })
    return () => { cancelled = true }
  }, [])

  // Fetch the current RP month once, for the season shown in the region popup.
  useEffect(() => {
    let cancelled = false
    fetchCurrentMonth().then((month) => { if (!cancelled && month) setCurrentMonth(month) })
    return () => { cancelled = true }
  }, [])

  // Fetch the live weather snapshot when the weather layer is first activated,
  // then refresh it every 10 min while it stays active (weather advances daily
  // server-side, so this is just to catch the tick without a page reload).
  useEffect(() => {
    if (!mapLayers.weather) return
    let cancelled = false
    const load = () => fetchWeather().then((data) => {
      if (!cancelled && data) setWeatherData(data)
    })
    load()
    const id = setInterval(load, 10 * 60 * 1000)
    return () => { cancelled = true; clearInterval(id) }
  }, [mapLayers.weather])

  // The buildable structure catalogue (type/spec/level/cost) is small and
  // static — fetch once, used by the Construction Mode form.
  useEffect(() => {
    let cancelled = false
    fetchStructureTypeData().then((data) => { if (!cancelled) setStructureTypeData(data) }).catch((err) => {
      if (!cancelled) console.error('[StructureTypeData] fetch failed:', err)
    })
    // Housing densities/styles/qualities — same lifetime, drives the Logement form.
    fetchHousingOptions().then((data) => { if (!cancelled) setHousingOptions(data) }).catch((err) => {
      if (!cancelled) console.error('[HousingOptions] fetch failed:', err)
    })
    return () => { cancelled = true }
  }, [])

  // Fetch the structures list for the logged-in user's country only when needed
  // (overlay shown or Construction Mode active), refreshed every 5 min while active.
  useEffect(() => {
    if ((!showStructures && !constructionMode) || !ownedCountryId) return
    let cancelled = false
    const load = () => fetchCountryStructures(ownedCountryId).then((data) => {
      if (!cancelled) {
        setStructuresData(data)
      }
    }).catch((err) => {
      if (!cancelled) console.error('[Structures] fetch failed:', err)
    })
    load()
    const id = setInterval(load, 5 * 60 * 1000)
    return () => { cancelled = true; clearInterval(id) }
  }, [showStructures, constructionMode, ownedCountryId])

  // Military deployments: own country only (positions are private, the
  // server scopes the list to the SSO session). Fetched while the overlay is
  // on, refreshed every minute so live updates from the Commandement show up.
  useEffect(() => {
    if (!showMilitary || !ownedCountryId) { setMilitaryUnits([]); return }
    let cancelled = false
    const load = () => fetchMilitaryDeployments().then((data) => {
      if (!cancelled) setMilitaryUnits(data)
    }).catch((err) => {
      if (!cancelled) console.error('[Military] fetch failed:', err)
    })
    load()
    const id = setInterval(load, 60 * 1000)
    return () => { cancelled = true; clearInterval(id) }
  }, [showMilitary, milRefresh, ownedCountryId])

  // Own ORBAT (deploy panel) + own plans, while the military overlay is on.
  useEffect(() => {
    if (!showMilitary || !ownedCountryId) return
    let cancelled = false
    fetchOrbat(ownedCountryId).then((data) => {
      if (cancelled) return
      const flat = []
      const walk = (nodes) => nodes.forEach((n) => { flat.push(n); walk(n.children || []) })
      walk(data.tree || [])
      setMyOrbatUnits(flat)
    }).catch(() => {})
    fetchPlans(ownedCountryId).then((p) => { if (!cancelled) setMilPlans(p) }).catch(() => {})
    return () => { cancelled = true }
  }, [showMilitary, ownedCountryId, milRefresh])

  // Saved designer symbols (free symbol placement in plans).
  useEffect(() => {
    if (!milPlanningOpen || !ownedCountryId) return
    let cancelled = false
    fetchSymbols(ownedCountryId).then((s) => { if (!cancelled) setMySymbols(s) }).catch(() => {})
    return () => { cancelled = true }
  }, [milPlanningOpen, ownedCountryId])

  // While a plan draw tool is armed, region clicks must NOT open the region
  // info popup (it interrupted arrow/zone placement and erasing). Window
  // bridge: MapView's click handler is registered once.
  useEffect(() => {
    useMapStore.setState({ milPlanToolActive: !!(activePlan && !readOnlyPlan && planTool) })
  }, [activePlan, readOnlyPlan, planTool])

  // Shared plan via ?milplan=TOKEN → read-only view.
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('milplan')
    if (!token) return
    fetchSharedPlan(token).then((plan) => {
      if (plan) { setActivePlan(plan); setReadOnlyPlan(true) }
    }).catch(() => {})
  }, [])

  // Plan de guerre ouvert : les attributions globales (déploiements réels)
  // des unités placées dans le plan sont masquées ; les autres restent
  // visibles en opacité réduite.
  const warPlanOpen = !!(activePlan && !readOnlyPlan)
  const plannedUnitIds = useMemo(() => {
    if (!activePlan) return new Set()
    return new Set((activePlan.features || []).map((f) => f.unit_id).filter(Boolean))
  }, [activePlan])

  // Category-filtered units for the map layer.
  const visibleMilitaryUnits = useMemo(
    () => militaryUnits
      .filter((u) => milCategories[unitCategory(u)] !== false)
      .filter((u) => !(warPlanOpen && plannedUnitIds.has(u.id)))
      .map((u) => (warPlanOpen ? { ...u, _dimmed: true } : u)),
    [militaryUnits, milCategories, warPlanOpen, plannedUnitIds]
  )

  // Drag & drop from the panel. With a plan open (war planification) the unit
  // becomes a plan feature on the active calque — the real deployment is
  // untouched. Without a plan it is a live deployment.
  const handleUnitDropped = useCallback(async (unitId, lngLat) => {
    if (!ownedCountryId) return
    if (activePlan && !readOnlyPlan) {
      const unit = myOrbatUnits.find((u) => u.id === unitId)
      if (!unit) return
      const sidc = unit.symbol?.sidc
        || `10031000${unit.echelon?.sidc_amplifier || '00'}1211000000`
      setActivePlan((p) => ({
        ...p,
        features: [...p.features, {
          feature_type: 'marker', sidc, label: unit.name, unit_id: unit.id, layer: planLayer,
          geometry: JSON.stringify([lngLat.lng, lngLat.lat]),
        }],
      }))
      setPlanDirty(true)
      return
    }
    try {
      await deployUnit(ownedCountryId, unitId, lngLat.lat, lngLat.lng)
      setMilRefresh((v) => v + 1)
    } catch (err) {
      console.error('[Military] deploy failed:', err)
    }
  }, [ownedCountryId, activePlan, readOnlyPlan, myOrbatUnits, planLayer])

  const handleUnitMoved = useCallback(async (unit, lngLat) => {
    if (!ownedCountryId) return
    try {
      await deployUnit(ownedCountryId, unit.id, lngLat.lat, lngLat.lng)
      setMilRefresh((v) => v + 1)
    } catch (err) {
      console.error('[Military] move failed:', err)
    }
  }, [ownedCountryId])

  // ── Plan editing ──────────────────────────────────────────────────────
  const handlePlanClick = useCallback((lngLat) => {
    if (!activePlan || readOnlyPlan || !planTool) return
    const pt = [lngLat.lng, lngLat.lat]
    if (planTool === 'symbol') {
      const sidc = planSymbol || '10031000001211000000'
      setActivePlan((p) => ({
        ...p,
        features: [...p.features, { feature_type: 'marker', sidc, layer: planLayer, geometry: JSON.stringify(pt) }],
      }))
      setPlanDirty(true)
    } else if (planTool === 'label') {
      const label = window.prompt('Texte :')
      if (!label) return
      setActivePlan((p) => ({
        ...p,
        features: [...p.features, { feature_type: 'label', label, color: planColor, layer: planLayer, geometry: JSON.stringify(pt) }],
      }))
      setPlanDirty(true)
    } else if (planTool === 'line' || planTool === 'polygon' || planTool === 'arrow') {
      setPlanDraft((d) => [...d, pt])
    } else if (planTool === 'erase') {
      // erase nearest feature within a small radius
      setActivePlan((p) => {
        let best = null; let bestDist = 0.5
        p.features.forEach((f, i) => {
          try {
            const coords = JSON.parse(f.geometry)
            const pts = Array.isArray(coords[0]) ? coords : [coords]
            pts.forEach(([lon, lat]) => {
              const d = Math.hypot(lon - pt[0], lat - pt[1])
              if (d < bestDist) { bestDist = d; best = i }
            })
          } catch { /* skip */ }
        })
        if (best === null) return p
        const features = p.features.slice()
        features.splice(best, 1)
        setPlanDirty(true)
        return { ...p, features }
      })
    }
  }, [activePlan, readOnlyPlan, planTool, planLayer, planColor, planSymbol])

  // Tool switch finalizes an in-progress line/polygon/arrow, carrying the
  // current draw style (width/dash/arrow heads).
  const handleSetPlanTool = useCallback((tool) => {
    if (planDraft.length >= 2 && (planTool === 'line' || planTool === 'polygon' || planTool === 'arrow')) {
      const type = planTool
      const style = JSON.stringify({
        width: planStyle.width || 'normal',
        dash: planStyle.dash || 'solid',
        ...(type === 'arrow' ? { heads: planStyle.heads ?? 1 } : {}),
      })
      setActivePlan((p) => ({
        ...p,
        features: [...p.features, { feature_type: type, color: planColor, style, layer: planLayer, geometry: JSON.stringify(planDraft) }],
      }))
      setPlanDirty(true)
    }
    setPlanDraft([])
    setPlanTool(tool)
  }, [planDraft, planTool, planColor, planLayer, planStyle])

  const handleSavePlan = useCallback(async () => {
    if (!activePlan || !ownedCountryId) return
    const saved = await updatePlan(ownedCountryId, activePlan.id, { features: activePlan.features })
    if (saved) { setActivePlan(saved); setPlanDirty(false) }
  }, [activePlan, ownedCountryId])

  const handleSharePlan = useCallback(async (plan) => {
    if (!ownedCountryId) return
    const updated = await sharePlan(ownedCountryId, plan.id, true)
    if (updated?.share_token) {
      const url = `${window.location.origin}${window.location.pathname}?milplan=${updated.share_token}`
      setActivePlan(updated)
      try { await navigator.clipboard.writeText(url) } catch { /* clipboard unavailable */ }
      window.prompt('Lien de partage (lecture seule, pour les bilans) :', url)
    }
  }, [ownedCountryId])

  // Calques of the active plan (multicalque): every distinct layer name.
  const planLayers = useMemo(() => {
    const names = new Set([planLayer])
    if (activePlan) (activePlan.features || []).forEach((f) => names.add(f.layer || 'Calque 1'))
    return [...names]
  }, [activePlan, planLayer])

  // Features shown on the map: visible calques + in-progress draft polyline.
  const milPlanFeatures = useMemo(() => {
    if (!activePlan) return []
    const feats = (activePlan.features || [])
      .filter((f) => !hiddenPlanLayers.includes(f.layer || 'Calque 1'))
    if (planDraft.length >= 2) {
      return [...feats, { feature_type: 'line', color: '#D5B654', geometry: JSON.stringify(planDraft) }]
    }
    return feats
  }, [activePlan, planDraft, hiddenPlanLayers])

  // Infrastructure type catalogue (cost_per_km) — small and static, fetch once.
  useEffect(() => {
    let cancelled = false
    fetchInfraTypes().then((data) => { if (!cancelled) setInfraTypes(data) }).catch(() => {})
    return () => { cancelled = true }
  }, [])

  // Header profile (country, player, balance). Refetched after each build /
  // infra creation so the displayed balance tracks spending.
  const refreshProfile = useCallback(() => {
    fetchMyProfile().then((data) => {
      setMyProfile(data && data.country_id ? data : null)
    }).catch(() => {})
  }, [])

  useEffect(() => { refreshProfile() }, [refreshProfile])

  // Built infrastructure with geometry — loaded when its overlay is visible
  // or Construction Mode is open, refreshed every 5 min while active.
  useEffect(() => {
    if (!showInfrastructure && !constructionMode) return
    let cancelled = false
    const load = () => fetchAllInfrastructure().then((data) => {
      if (!cancelled) setInfrastructureData(data)
    }).catch((err) => {
      if (!cancelled) console.error('[Infrastructure] fetch failed:', err)
    })
    load()
    const id = setInterval(load, 5 * 60 * 1000)
    return () => { cancelled = true; clearInterval(id) }
  }, [showInfrastructure, constructionMode])

  const hasOwnedData = !!ownedCountryId && !!regionStats && Object.keys(regionStats).length > 0

  // Handle regions data loaded from MapView (avoids double fetch)
  const handleRegionsDataLoaded = useCallback((data) => {
    setRegionsData(data)
    // Count unique countries
    const uniqueCountries = new Set()
    data.features.forEach(feature => {
      if (feature.properties.country_id) {
        uniqueCountries.add(feature.properties.country_id)
      }
    })
    setActiveCountriesCount(uniqueCountries.size)
  }, [])

  const toggleLayer = (layerName, forceValue) => {
    // forceValue === false → neutral gray map (all layers off).
    // Otherwise set only the selected layer to true (radio behavior).
    setMapLayers(buildLayerState(forceValue === false ? null : layerName))
  }

  const handleStatTypeChange = (group, value) => {
    setStatType((prev) => ({ ...prev, [group]: value }))
  }

  const handleStructFilterChange = (field, value) => {
    setStructFilter((prev) => ({ ...prev, [field]: value }))
  }

  const updateFilter = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }))
  }

  // Plan Mode functions
  const handleRegionPaint = (regionId, geographicalArea) => {
    // Read live values from the store (fresh inside the once-registered handler).
    const { selectedColor: currentColor, selectionMode: currentMode, planModeActive } = mapState()

    console.log('handleRegionPaint called:', {
      regionId,
      geographicalArea,
      currentMode,
      currentColor
    })

    if (!planModeActive) {
      console.log('Rejected: Plan Mode not active (store check)')
      return
    }

    if (currentMode === 'single') {
      console.log('Setting single region color:', regionId, currentColor)
      setCustomColors(prev => {
        const newColors = {
          ...prev,
          [regionId]: currentColor
        }
        console.log('New customColors:', newColors)
        return newColors
      })
    } else if (currentMode === 'area' && geographicalArea) {
      console.log('Setting area color:', geographicalArea, currentColor)
      // Color all regions in the same geographical area
      // This will be handled in MapView by passing the geographical_area string
      setCustomColors(prev => {
        const newColors = {
          ...prev,
          [`area_${geographicalArea}`]: currentColor
        }
        console.log('New customColors:', newColors)
        return newColors
      })
    }
  }

  const handleRegionErase = (regionId, geographicalArea) => {
    const { selectionMode: currentMode, planModeActive } = mapState()

    console.log('handleRegionErase called:', {
      regionId,
      geographicalArea,
      currentMode
    })

    if (!planModeActive) {
      return
    }

    if (currentMode === 'single') {
      // Erase single region
      setCustomColors(prev => {
        const newColors = { ...prev }
        delete newColors[regionId]
        return newColors
      })
    } else if (currentMode === 'area' && geographicalArea) {
      // Erase entire geographical area
      setCustomColors(prev => {
        const newColors = { ...prev }
        delete newColors[`area_${geographicalArea}`]
        return newColors
      })
    }
  }

  const handleClearAll = () => {
    if (confirm('Voulez-vous vraiment réinitialiser toutes les couleurs ?')) {
      setCustomColors({})
    }
  }

  // Plan Mode and Construction Mode both hijack map clicks — keep them
  // mutually exclusive so a click has one unambiguous meaning.
  const handleTogglePlanMode = () => {
    setPlanMode((prev) => {
      const next = !prev
      if (next) setConstructionMode(false)
      return next
    })
  }

  // Planification militaire (cartes d'état-major) : bouton dédié — ouvre
  // l'overlay militaire + la section plans du panneau. Fermer referme le
  // plan actif pour restaurer l'affichage normal des unités.
  const handleToggleMilitaryPlanning = () => {
    setMilPlanningOpen((prev) => {
      const next = !prev
      if (next) setShowMilitary(true)
      else {
        setShowMilitary(false)
        setActivePlan(null); setPlanTool(null); setPlanDraft([])
        setPlanDirty(false); setHiddenPlanLayers([])
      }
      return next
    })
  }

  const handleToggleConstructionMode = () => {
    setConstructionMode((prev) => {
      const next = !prev
      if (next) setPlanMode(false)
      return next
    })
    setInfrastructurePath([])
    setConstructionStatus({ state: 'idle', message: '' })
  }

  // Switch between the structure and infra tools inside Construction Mode.
  // The in-progress path is kept when hopping to structures and back —
  // clearing it is what the "Annuler" button is for.
  const handleConstructionToolChange = (tool) => {
    setConstructionTool(tool)
    setConstructionStatus({ state: 'idle', message: '' })
  }

  const handleConstructionSelectionChange = (field, value) => {
    setConstructionSelection((prev) => {
      const next = { ...prev, [field]: value }
      // Reset dependent fields when a higher-level choice changes
      if (field === 'type') { next.specialisation = ''; next.level = null }
      if (field === 'specialisation') { next.level = null }
      return next
    })
  }

  const handleToggleNotifySecret = () => {
    setNotifySecret((prev) => {
      const next = !prev
      try { localStorage.setItem(NOTIFY_SECRET_KEY, next ? '1' : '0') } catch { /* private mode */ }
      return next
    })
  }

  // A Logement is fully described by its housing fields — the level/spec
  // selectors don't apply to it (structures_datas' Logement rows are a legacy
  // artefact the real housing system never used).
  const housingSelectionComplete = (sel) =>
    !!(sel?.density_id && sel?.style_id && sel?.quality_id && sel?.capacity > 0)

  // Resolve construction cost from catalogue data. Reads the catalogue from
  // the window bridge, NOT the closure: this is called from handleStructurePlace,
  // whose first instance is what MapView's once-registered click handler holds —
  // its closure would forever see the pre-fetch empty catalogue (cost 0).
  const resolveConstructionCost = (selection) => {
    const { type, specialisation, level } = selection
    if (type === 'Logement') {
      const options = mapState().housingOptions || housingOptions
      return housingCost(options, selection) ?? 0
    }
    const catalogue = mapState().structureTypeData || structureTypeData
    const entry = catalogue.find(
      (d) => d.type === type && d.specialisation === (specialisation || 'NA') && d.level === level
    )
    return entry?.cout_construction ?? 0
  }

  // Click-to-build handler passed to MapView. Region ownership is checked
  // here for a fast local error, but the server re-verifies it independently
  // (StructureRepository.verify_region_ownership) — this check is UX only,
  // not the security boundary.
  const handleStructurePlace = async (region, lngLat) => {
    const s = mapState()
    if (!s.constructionModeActive) return

    const currentOwnedCountryId = s.ownedCountryId
    if (!currentOwnedCountryId) {
      setConstructionStatus({ state: 'error', message: 'Connectez-vous pour construire.' })
      return
    }
    if (region.country_id !== currentOwnedCountryId) {
      setConstructionStatus({ state: 'error', message: '❌ Cette région ne vous appartient pas.' })
      return
    }
    const selection = s.constructionSelection
    const { type } = selection
    if (!type) {
      setConstructionStatus({ state: 'error', message: '❌ Choisissez un type de structure.' })
      return
    }
    if (type === 'Logement') {
      if (!housingSelectionComplete(selection)) {
        setConstructionStatus({ state: 'error', message: '❌ Choisissez densité, style, qualité et capacité.' })
        return
      }
    } else if (!selection.level) {
      setConstructionStatus({ state: 'error', message: '❌ Choisissez un niveau de structure.' })
      return
    }

    const cost = resolveConstructionCost(selection)

    if (s.skipConstructionConfirm) {
      // Skip modal, build directly
      await executeConstruction(region, lngLat, selection, cost)
    } else {
      // Show confirmation modal
      setPendingConstruction({ region, lngLat, selection, cost })
    }
  }

  // Actual construction execution (called after modal confirmation or skip)
  const executeConstruction = async (region, lngLat, selection, cost) => {
    const { type, specialisation, level } = selection
    const isHousing = type === 'Logement'
    setConstructionStatus({ state: 'pending', message: 'Construction en cours…' })
    try {
      const result = await constructStructure({
        region_id: region.region_id,
        structure_type: type,
        specialisation: specialisation || 'NA',
        level,
        density_id: selection.density_id,
        style_id: selection.style_id,
        quality_id: selection.quality_id,
        capacity: selection.capacity,
        notify_secret: mapState().notifySecret,
        world_x: lngLat.lng,
        world_y: lngLat.lat,
        world_z: 0,
      })
      const newId = result.structure_ids?.[0]
      setStructuresData((prev) => [
        ...prev,
        {
          id: newId,
          region_id: region.region_id,
          region_name: region.name,
          type,
          specialisation: isHousing ? 'NA' : (specialisation || 'NA'),
          level: isHousing ? 1 : level,
          capacity: isHousing ? selection.capacity : null,
          country_id: mapState().ownedCountryId,
          world_x: lngLat.lng,
          world_y: lngLat.lat,
          world_z: 0,
        },
      ])
      const label = isHousing
        ? `Logement pour ${Number(selection.capacity).toLocaleString('fr-FR')} hab. (confort ${result.comfort}/100)`
        : `${type} niv.${level}`
      setConstructionStatus({
        state: 'success',
        message: `✅ ${label} construit (#${newId}) — ${Number(result.total_cost || 0).toLocaleString('fr-FR')} crédits`,
      })
      refreshProfile() // header balance tracks the spend
    } catch (err) {
      setConstructionStatus({ state: 'error', message: `❌ ${err.message}` })
    }
  }

  const handleConfirmConstruction = async () => {
    if (!pendingConstruction) return
    const { region, lngLat, selection, cost } = pendingConstruction
    setPendingConstruction(null)
    await executeConstruction(region, lngLat, selection, cost)
  }

  const handleCancelConstruction = () => {
    setPendingConstruction(null)
  }

  // Infrastructure drawing: a click adds a waypoint; holding the click and
  // moving free-draws the path (MapView samples nodes along the cursor).
  // Several nodes inside the same region are allowed — the path is real
  // geometry now, not a list of regions.
  // Perpendicular deviation of b from segment a→c relative to |ac| — used to
  // drop points that barely bend the line ("auto-straighten"). Scale
  // invariant, so it behaves the same at every zoom.
  const isNearlyCollinear = (a, b, c) => {
    const acx = c.lng - a.lng
    const acy = c.lat - a.lat
    const len = Math.hypot(acx, acy)
    if (len < 1e-9) return true
    const cross = Math.abs(acx * (b.lat - a.lat) - acy * (b.lng - a.lng))
    return cross / len < 0.04 * len
  }

  const handleInfrastructureClick = (region, lngLat) => {
    const s = mapState()
    if (!s.infrastructureModeActive) return

    const currentOwnedCountryId = s.ownedCountryId
    if (!currentOwnedCountryId) {
      setConstructionStatus({ state: 'error', message: 'Connectez-vous pour construire des infrastructures.' })
      return
    }
    if (region.country_id !== currentOwnedCountryId) {
      setConstructionStatus({ state: 'error', message: '❌ Cette région ne vous appartient pas — point ignoré.' })
      return
    }

    setInfrastructurePath((prev) => {
      const seg = prev.length ? (prev[prev.length - 1].seg || 0) : 0
      const inSeg = prev.filter((p) => (p.seg || 0) === seg)
      const last = inSeg[inSeg.length - 1]
      // Duplicate of the last node (double-click) — ignore.
      if (last && Math.abs(last.lngLat.lng - lngLat.lng) < 1e-9 && Math.abs(last.lngLat.lat - lngLat.lat) < 1e-9) {
        return prev
      }
      // Auto-straighten: if the previous node barely deviates from the line
      // between its predecessor and the new node, replace it instead of
      // stacking near-collinear points (free-draw would otherwise pile them up).
      const beforeLast = inSeg[inSeg.length - 2]
      if (last && beforeLast && isNearlyCollinear(beforeLast.lngLat, last.lngLat, lngLat)) {
        const lastGlobalIdx = prev.lastIndexOf(last)
        const next = [...prev]
        next[lastGlobalIdx] = { region, lngLat, seg }
        setSelectedInfraPoint(lastGlobalIdx)
        return next
      }
      setSelectedInfraPoint(prev.length)
      return [...prev, { region, lngLat, seg }]
    })
  }

  const handleFinishInfrastructure = async () => {
    // Segments with ≥2 points are what actually gets built; a lone fork
    // anchor that never grew is dropped silently.
    const segments = editorPathSegments(infrastructurePath).filter((s) => s.length >= 2)
    if (segments.length === 0) {
      setConstructionStatus({ state: 'error', message: '❌ Tracez au moins 2 points pour une infrastructure.' })
      return
    }
    // Single segment keeps the legacy flat shape; branched networks send the
    // nested form (the server + renderer understand both).
    const path = segments.length === 1 ? segments[0] : segments

    // Rework of an existing route: free within the owned distance, extra km
    // beyond it are billed server-side at the row's cost_per_km.
    if (editingInfra) {
      setConstructionStatus({ state: 'pending', message: `Mise à jour du tracé #${editingInfra.id}…` })
      try {
        const result = await updateInfrastructurePath(editingInfra.id, path)
        const extension = result.charged_cost
          ? ` — extension facturée ${Number(result.charged_cost).toLocaleString('fr-FR')} crédits`
          : ''
        setConstructionStatus({
          state: 'success',
          message: `✅ Tracé #${result.infra_id} mis à jour — ${Number(result.drawn_km).toLocaleString('fr-FR')} / ${Number(result.owned_km).toLocaleString('fr-FR')} km${extension}`,
        })
        setInfrastructurePath([])
        setEditingInfra(null)
        fetchAllInfrastructure().then(setInfrastructureData).catch(() => {})
        refreshProfile()
      } catch (err) {
        setConstructionStatus({ state: 'error', message: `❌ ${err.message}` })
      }
      return
    }

    const { type } = infrastructureSelection
    if (!type) {
      setConstructionStatus({ state: 'error', message: '❌ Choisissez un type d\'infrastructure.' })
      return
    }

    setConstructionStatus({ state: 'pending', message: 'Construction de l\'infrastructure en cours…' })
    try {
      // Anchor region = where the path starts. length_km and cost are
      // recomputed server-side from the geometry (haversine).
      const result = await constructInfrastructure({
        region_id: infrastructurePath[0].region.region_id,
        infra_type: type,
        path,
        notify_secret: notifySecret,
      })
      setConstructionStatus({
        state: 'success',
        message: `✅ ${result.type} #${result.infra_id} — ${Number(result.length_km).toLocaleString('fr-FR')} km — ${Number(result.total_cost).toLocaleString('fr-FR')} crédits`,
      })
      setInfrastructurePath([])
      // Refresh the lines overlay with the newly built route + the balance
      fetchAllInfrastructure().then(setInfrastructureData).catch(() => {})
      refreshProfile()
    } catch (err) {
      setConstructionStatus({ state: 'error', message: `❌ ${err.message}` })
    }
  }

  // Drag & drop persistence for Construction Mode. The structure must stay
  // inside its own region — region_id is the gameplay anchor and never
  // changes from a map drag.
  const handleStructureMove = async (structProps, lngLat, dropRegion) => {
    if (!mapState().constructionModeActive) return
    if (!dropRegion || dropRegion.region_id !== structProps.region_id) {
      setConstructionStatus({
        state: 'error',
        message: `❌ La structure #${structProps.id} doit rester dans sa région (${structProps.region_name || structProps.region_id}).`,
      })
      return
    }
    setConstructionStatus({ state: 'pending', message: `Déplacement de #${structProps.id}…` })
    try {
      const moved = await moveStructure(structProps.id, lngLat.lng, lngLat.lat)
      setStructuresData((prev) => prev.map((s) => (
        s.id === moved.id ? { ...s, world_x: moved.world_x, world_y: moved.world_y } : s
      )))
      setConstructionStatus({ state: 'success', message: `✅ Structure #${moved.id} déplacée.` })
    } catch (err) {
      setConstructionStatus({ state: 'error', message: `❌ ${err.message}` })
    }
  }

  const handleCancelInfrastructure = () => {
    setInfrastructurePath([])
    setEditingInfra(null)
  }

  // Click on one of your own existing routes (infra tool, nothing being
  // drawn) → load its path into the editor for rework. Uses the window
  // bridge for the same first-render-closure reason as everything else.
  const handleInfraEditStart = (infraProps) => {
    const s = mapState()
    if (!s.infrastructureModeActive) return
    if ((s.infrastructurePath || []).length > 0) return // mid-draw: ignore
    const full = (s.infrastructureData || []).find((i) => i.id === infraProps.id)
    if (!full || !Array.isArray(full.path)) return

    setEditingInfra({ id: full.id, type: full.type, owned_km: full.length_km })
    const region = { region_id: full.region_id, name: full.region_name, country_id: s.ownedCountryId }
    // Stored path may be flat (single line) or nested (branched network) —
    // rebuild the seg-tagged editor points either way.
    setInfrastructurePath(pathSegments(full.path).flatMap((coords, seg) =>
      coords.map(([lng, lat]) => ({ region, lngLat: { lng, lat }, seg }))
    ))
    setConstructionStatus({
      state: 'pending',
      message: `✏️ Modification du tracé #${full.id} (${full.type}) — budget ${Number(full.length_km).toLocaleString('fr-FR')} km (dépassement facturé)`,
    })
  }

  // Path corrections while drawing: drag a node to move it, right-click to
  // delete it (both from MapView), undo button removes the last one. Moving
  // a junction node (a coordinate shared by several segments — see
  // handleInfraPointFork) moves every co-located point together, so branch
  // networks stay welded.
  const handleInfraPointMove = (index, lngLat) => {
    setSelectedInfraPoint(index)
    setInfrastructurePath((prev) => {
      const anchor = prev[index]
      if (!anchor) return prev
      return prev.map((p, i) => {
        if (i === index) return { ...p, lngLat }
        const linked = Math.abs(p.lngLat.lng - anchor.lngLat.lng) < 1e-9
          && Math.abs(p.lngLat.lat - anchor.lngLat.lat) < 1e-9
        return linked ? { ...p, lngLat } : p
      })
    })
  }

  const handleInfraPointDelete = (index) => {
    setInfrastructurePath((prev) => prev.filter((_, i) => i !== index))
  }

  const handleUndoInfraPoint = () => {
    setInfrastructurePath((prev) => prev.slice(0, -1))
  }

  // Fork: plain click on an existing node starts a new branch anchored
  // there (duplicated coordinates = the junction; drag keeps them welded).
  // Subsequent clicks extend the new branch — tri-/quadri-nodes emerge from
  // forking the same junction repeatedly.
  const handleInfraPointFork = (index) => {
    setInfrastructurePath((prev) => {
      const p = prev[index]
      if (!p) return prev
      const newSeg = prev.reduce((m, q) => Math.max(m, q.seg || 0), 0) + 1
      setSelectedInfraPoint(prev.length)
      return [...prev, { region: p.region, lngLat: { ...p.lngLat }, seg: newSeg }]
    })
  }

  // Full redraw: wipe the drawn points but stay in edit mode (same budget) —
  // for when correcting node-by-node is slower than starting over.
  const handleRestartInfrastructure = () => {
    setInfrastructurePath([])
    setSelectedInfraPoint(null)
  }

  // Ruler: same click-to-place mechanic as infra path drawing, no ownership
  // or cost — just distance. Toggling off clears the drawn path.
  const handleToggleRulerMode = () => {
    setRulerMode((prev) => {
      const next = !prev
      if (!next) { setRulerPath([]); setSelectedRulerPoint(null) }
      return next
    })
  }

  const handleRulerClick = (lngLat) => {
    setSelectedRulerPoint(rulerPath.length)
    setRulerPath((prev) => [...prev, { lngLat }])
  }

  const handleUndoRulerPoint = () => {
    setRulerPath((prev) => prev.slice(0, -1))
    setSelectedRulerPoint((i) => (i != null ? Math.max(0, i - 1) : null))
  }

  const handleClearRuler = () => {
    setRulerPath([])
    setSelectedRulerPoint(null)
  }

  const handleRulerPointMove = (index, lngLat) => {
    setSelectedRulerPoint(index)
    setRulerPath((prev) => prev.map((p, i) => (i === index ? { ...p, lngLat } : p)))
  }

  const handleRulerPointDelete = (index) => {
    setRulerPath((prev) => prev.filter((_, i) => i !== index))
    setSelectedRulerPoint(null)
  }

  const handleExportPNG = () => {
    // This will be handled by MapView
    const event = new CustomEvent('export-map-png')
    window.dispatchEvent(event)
  }

  const isColorMap = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return false
    }

    const entries = Object.entries(value)
    if (entries.length === 0) {
      return false
    }

    return entries.every(([key, color]) => {
      const validKey = /^\d+$/.test(key) || key.startsWith('area_')
      return validKey && typeof color === 'string'
    })
  }

  const extractImportedColors = (payload) => {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return null
    }

    // Legacy format: plain color map { "123": "#FF0000", "area_X": "#00FF00" }
    if (isColorMap(payload)) {
      return payload
    }

    // V2 format: { colors: {...} }
    if (isColorMap(payload.colors)) {
      return payload.colors
    }

    // Compatibility alias: { customColors: {...} }
    if (isColorMap(payload.customColors)) {
      return payload.customColors
    }

    // Multi-plan format: { plans: { name: { colors: {...} } } }
    if (payload.plans && typeof payload.plans === 'object' && !Array.isArray(payload.plans)) {
      const merged = {}

      Object.values(payload.plans).forEach((plan) => {
        if (!plan || typeof plan !== 'object') {
          return
        }

        const planColors = isColorMap(plan.colors)
          ? plan.colors
          : (isColorMap(plan) ? plan : null)

        if (!planColors) {
          return
        }

        Object.entries(planColors).forEach(([key, color]) => {
          merged[key] = color
        })
      })

      if (Object.keys(merged).length > 0) {
        return merged
      }
    }

    return null
  }

  const handleExportJSON = () => {
    const payload = {
      version: 2,
      type: 'resurgence-plan-colors',
      exportedAt: new Date().toISOString(),
      plans: {
        principal: {
          name: 'Plan principal',
          colors: customColors
        }
      }
    }

    const dataStr = JSON.stringify(payload, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = `map-plan-${new Date().toISOString().split('T')[0]}.json`
    link.click()
    URL.revokeObjectURL(url)
  }

  const handleImportJSON = (data) => {
    try {
      const extractedColors = extractImportedColors(data)

      if (!extractedColors) {
        throw new Error('unsupported-json-format')
      }

      setCustomColors(extractedColors)
      showToast(`Configuration importée : ${Object.keys(extractedColors).length} zones/régions coloriées`, 'success')
    } catch {
      showToast('Erreur lors de l\'importation du fichier', 'error')
    }
  }

  const handleSetCenterRegionHandler = (handler) => {
    setCenterToRegionHandler(() => handler)
  }

  const handleCenterMap = (region) => {
    if (centerToRegionHandler && region) {
      centerToRegionHandler(region)
    }
  }

  const handleShareLocation = (region) => {
    if (!region) return
    const url = `${window.location.origin}${window.location.pathname}?region=${region.region_id}`
    navigator.clipboard.writeText(url).then(() => {
      showToast('Lien copié dans le presse-papier', 'success')
    }).catch(() => {
      showToast('Erreur lors de la copie du lien', 'error')
    })
  }

  // ── Full map-state capture / restore (bookmarks + share links) ──────────
  const assembleCurrentState = useCallback(() => assembleState({
    viewport: mapState().viewport,
    isGlobeView,
    activeLayer: ALL_LAYER_KEYS.find((k) => mapLayers[k]) || null,
    filters,
    border: { mode: borderMode, width: borderWidth, color: borderColor, opacity: borderOpacity, style: borderStyle },
    org: selectedOrgId,
    region: selectedRegion?.region_id ?? null,
  }), [isGlobeView, mapLayers, filters, borderMode, borderWidth, borderColor, borderOpacity, borderStyle, selectedOrgId, selectedRegion])

  const applyMapState = useCallback((obj) => {
    if (!obj) return
    if (obj.proj) setIsGlobeView(obj.proj === 'globe')
    if (obj.layer !== undefined) setMapLayers(buildLayerState(obj.layer))
    if (obj.filters) setFilters(obj.filters)
    if (obj.border) {
      if (obj.border.mode != null) setBorderMode(obj.border.mode)
      if (obj.border.width != null) setBorderWidth(obj.border.width)
      if (obj.border.color != null) setBorderColor(obj.border.color)
      if (obj.border.opacity != null) setBorderOpacity(obj.border.opacity)
      if (obj.border.style != null) setBorderStyle(obj.border.style)
    }
    if (obj.org !== undefined) setSelectedOrgId(obj.org)
    if (obj.region != null && regionsData) {
      const feat = regionsData.features.find((f) => f.properties.region_id === obj.region)
      if (feat) setSelectedRegion(feat.properties)
    }
    if (Array.isArray(obj.c)) {
      useMapStore.setState({ flyTo: { center: obj.c, zoom: obj.z, bearing: obj.b } })
    }
  }, [regionsData])

  const handleShareView = useCallback(() => {
    const url = buildShareUrl(assembleCurrentState())
    navigator.clipboard.writeText(url)
      .then(() => showToast('Lien de la vue copié (état complet)', 'success'))
      .catch(() => window.prompt('Lien de la vue :', url))
  }, [assembleCurrentState, showToast])

  // Exports (S8): image (PNG/PDF via MapView) + full session JSON (restorable).
  const handleExportImage = useCallback((format) => {
    window.dispatchEvent(new CustomEvent('map-export', { detail: { format } }))
  }, [])

  const handleExportSession = useCallback(() => {
    const payload = {
      version: 2,
      type: 'resurgence-map-session',
      exportedAt: new Date().toISOString(),
      state: assembleCurrentState(),
      customColors,
    }
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `map-session-${new Date().toISOString().split('T')[0]}.json`
    link.click()
    URL.revokeObjectURL(url)
  }, [assembleCurrentState, customColors])

  // Restore a full shared state from ?s= once the map + data are ready.
  const sharedStateApplied = useMemo(() => ({ done: false }), [])
  useEffect(() => {
    if (sharedStateApplied.done || !mapLoaded || !regionsData) return
    const raw = new URLSearchParams(window.location.search).get(SHARE_PARAM)
    if (!raw) return
    const obj = decodeState(raw)
    if (obj) {
      applyMapState(obj)
      window.history.replaceState({}, '', window.location.pathname)
    }
    sharedStateApplied.done = true
  }, [mapLoaded, regionsData, applyMapState, sharedStateApplied])

  // Parse URL parameters on mount to center on region
  useEffect(() => {
    if (!centerToRegionHandler || !mapLoaded || !regionsData) return
    
    const params = new URLSearchParams(window.location.search)
    const regionIdParam = params.get('region')
    
    if (regionIdParam) {
      // Use shared regionsData instead of fetching again
      const feature = regionsData.features.find(f => f.properties.region_id === parseInt(regionIdParam))
      if (feature) {
        // Add delay to ensure map is fully initialized
        setTimeout(() => {
          centerToRegionHandler(feature.properties)
          // Clear URL parameter after centering
          window.history.replaceState({}, '', window.location.pathname)
        }, 500)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centerToRegionHandler, mapLoaded, regionsData])

  // Weather shown on the map/popups: the timeline override when a past month is
  // selected, otherwise the live snapshot.
  const displayWeather = timelineWeather || weatherData

  return (
    <div className="app">
      {!mapLoaded && <LoadingScreen />}

      {myProfile && <TopBar profile={myProfile} />}

      <ToastStack toasts={toasts} onDismiss={dismissToast} />

      {mapLoaded && <MultiSelectPanel regionsData={regionsData} weatherData={displayWeather} />}

      {mapLoaded && <WeatherTimeline active={!!mapLayers.weather} onWeatherData={setTimelineWeather} />}

      <SettingsPanel
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        theme={theme}
        onToggleTheme={toggleTheme}
        isLoggedIn={!!myProfile}
        loginUrl={buildLoginUrl()}
        logoutUrl={buildLogoutUrl()}
        hiddenSections={hiddenSections}
        onToggleSection={toggleSection}
        infraTypes={infraTypes}
        borderMode={borderMode}
        onBorderModeChange={setBorderMode}
        borderWidth={borderWidth}
        onBorderWidthChange={setBorderWidth}
        borderColor={borderColor}
        onBorderColorChange={setBorderColor}
        borderOpacity={borderOpacity}
        onBorderOpacityChange={setBorderOpacity}
        borderStyle={borderStyle}
        onBorderStyleChange={setBorderStyle}
        haloEnabled={haloEnabled}
        onToggleHalo={() => setHaloEnabled((v) => !v)}
        haloColor={haloColor}
        onHaloColorChange={setHaloColor}
        bubbleColor={bubbleColor}
        onBubbleColorChange={setBubbleColor}
        bubbleSize={bubbleSize}
        onBubbleSizeChange={setBubbleSize}
        overlayStyles={overlayStyles}
        onUpdateOverlayStyle={updateOverlayStyle}
        onResetOverlayStyle={resetOverlayStyle}
        onUpdateInfraStyle={updateInfraStyle}
        onResetInfraStyle={resetInfraStyle}
      />

      <ActiveToolStatus
        rulerMode={rulerMode}
        rulerPath={rulerPath}
        constructionMode={constructionMode}
        constructionTool={constructionTool}
        infrastructurePath={infrastructurePath}
        editingInfra={editingInfra}
        planMode={planMode}
        coloredRegionsCount={Object.keys(customColors).length}
      />

      <CommandPalette
        open={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        regionsData={regionsData}
        organizationsCatalog={organizationsCatalog}
        onCenterMap={handleCenterMap}
        onSelectOrg={(id) => { setSelectedOrgId(id); toggleLayer('organizations') }}
        setVueNormale={() => { setSatelliteMode('none'); if (mapLayers.topography) toggleLayer(null, false) }}
        setVueSatellite={() => { setSatelliteMode('satellite'); if (mapLayers.topography) toggleLayer(null, false) }}
        setVueTopo={() => { setSatelliteMode('none'); toggleLayer('topography') }}
        onToggleLayer={toggleLayer}
        onBorderModeChange={setBorderMode}
        onToggleRulerMode={handleToggleRulerMode}
        onTogglePlanMode={handleTogglePlanMode}
        onToggleConstructionMode={handleToggleConstructionMode}
        onToggleGlobeView={() => setIsGlobeView((v) => !v)}
      />

      <SearchModal
        open={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        regionsData={regionsData}
        onCenterMap={handleCenterMap}
        organizationsCatalog={organizationsCatalog}
        weatherData={displayWeather}
      />

      <ShortcutHints
        onOpenSearch={() => { setSearchModalOpen(true); setCommandPaletteOpen(false) }}
        onOpenPalette={() => { setCommandPaletteOpen(true); setSearchModalOpen(false) }}
      />

      <Sidebar
        layers={mapLayers}
        filters={filters}
        onToggleLayer={toggleLayer}
        onUpdateFilter={updateFilter}
        planMode={planMode}
        onTogglePlanMode={handleTogglePlanMode}
        selectedColor={selectedColor}
        onColorChange={setSelectedColor}
        selectionMode={selectionMode}
        onSelectionModeChange={setSelectionMode}
        onClearAll={handleClearAll}
        onExportPNG={handleExportPNG}
        onExportJSON={handleExportJSON}
        onImportJSON={handleImportJSON}
        showToast={showToast}
        coloredRegionsCount={Object.keys(customColors).length}
        activeCountriesCount={activeCountriesCount}
        isGlobeView={isGlobeView}
        onToggleGlobeView={() => setIsGlobeView(!isGlobeView)}
        satelliteMode={satelliteMode}
        onSetSatelliteMode={setSatelliteMode}
        borderMode={borderMode}
        onBorderModeChange={setBorderMode}
        borderWidth={borderWidth}
        onBorderWidthChange={setBorderWidth}
        borderColor={borderColor}
        onBorderColorChange={setBorderColor}
        borderOpacity={borderOpacity}
        onBorderOpacityChange={setBorderOpacity}
        borderStyle={borderStyle}
        onBorderStyleChange={setBorderStyle}
        showBubbles={showBubbles}
        onToggleShowBubbles={() => setShowBubbles((v) => !v)}
        bubbleMetric={bubbleMetric}
        onBubbleMetricChange={setBubbleMetric}
        onAssembleState={assembleCurrentState}
        onApplyState={applyMapState}
        onShareView={handleShareView}
        onExportImage={handleExportImage}
        onExportSession={handleExportSession}
        rulerMode={rulerMode}
        onToggleRulerMode={handleToggleRulerMode}
        rulerPath={rulerPath}
        onUndoRulerPoint={handleUndoRulerPoint}
        onClearRuler={handleClearRuler}
        rulerCrowFlies={rulerCrowFlies}
        onRulerCrowFliesChange={setRulerCrowFlies}
        organizationsCatalog={organizationsCatalog}
        selectedOrgId={selectedOrgId}
        onSelectOrg={(id) => {
          if (id === selectedOrgId) {
            setSelectedOrgId(null)
            toggleLayer(null, false)
          } else {
            setSelectedOrgId(id)
            toggleLayer('organizations')
          }
        }}
        regionsData={regionsData}
        weatherData={displayWeather}
        onCenterMap={handleCenterMap}
        hasOwnedData={hasOwnedData}
        ownedCountryId={ownedCountryId}
        statMeta={statMeta}
        statType={statType}
        onStatTypeChange={handleStatTypeChange}
        structOptions={structOptions}
        structFilter={structFilter}
        onStructFilterChange={handleStructFilterChange}
        showStructures={showStructures}
        onToggleShowStructures={() => setShowStructures((v) => !v)}
        showInfrastructure={showInfrastructure}
        onToggleShowInfrastructure={() => setShowInfrastructure((v) => !v)}
        showCities={showCities}
        onToggleShowCities={() => setShowCities((v) => !v)}
        showRoads={showRoads}
        onToggleShowRoads={() => setShowRoads((v) => !v)}
        showRailways={showRailways}
        onToggleShowRailways={() => setShowRailways((v) => !v)}
        overlayStyles={overlayStyles}
        onUpdateOverlayStyle={updateOverlayStyle}
        onResetOverlayStyle={resetOverlayStyle}
        onUpdateInfraStyle={updateInfraStyle}
        onResetInfraStyle={resetInfraStyle}
        infrastructureData={infrastructureData}
        showMilitary={showMilitary}
        onToggleShowMilitary={() => setShowMilitary((v) => !v)}
        militaryPlanning={milPlanningOpen}
        onToggleMilitaryPlanning={handleToggleMilitaryPlanning}
        military={{
          categories: milCategories,
          onToggleCategory: (key) => setMilCategories((c) => ({ ...c, [key]: c[key] === false })),
          undeployedUnits: activePlan && !readOnlyPlan ? myOrbatUnits : myOrbatUnits.filter((u) => !u.position),
          planningMode: !!(activePlan && !readOnlyPlan),
          planningOpen: milPlanningOpen || readOnlyPlan,
          deployHighlightId,
          ownedCountryId,
          plans: milPlans,
          activePlan,
          planTool,
          readOnlyPlan,
          planDirty,
          onSelectPlan: async (p) => {
            const full = await fetchPlan(ownedCountryId, p.id)
            if (full) {
              setActivePlan(full); setReadOnlyPlan(false); setPlanDirty(false)
              setPlanLayer((full.features || []).find((f) => f.layer)?.layer || 'Calque 1')
              setHiddenPlanLayers([])
            }
          },
          onCreatePlan: async () => {
            const name = window.prompt('Nom du plan :', 'Plan d\'opération')
            if (!name) return
            const created = await createPlan(ownedCountryId, { name })
            if (created) {
              setMilPlans((l) => [created, ...l])
              setActivePlan(created)
              setReadOnlyPlan(false)
              setPlanDirty(false)
              setPlanLayer('Calque 1')
              setHiddenPlanLayers([])
            }
          },
          onDeletePlan: async (p) => {
            if (!window.confirm(`Supprimer le plan « ${p.name} » ?`)) return
            if (await apiDeletePlan(ownedCountryId, p.id)) {
              setMilPlans((l) => l.filter((x) => x.id !== p.id))
            }
          },
          onSharePlan: handleSharePlan,
          onClosePlan: () => { setActivePlan(null); setPlanTool(null); setPlanDraft([]); setPlanDirty(false); setHiddenPlanLayers([]) },
          onSetPlanTool: handleSetPlanTool,
          planLayers,
          activeLayer: planLayer,
          hiddenLayers: hiddenPlanLayers,
          onSelectLayer: setPlanLayer,
          onAddLayer: () => {
            const name = window.prompt('Nom du calque :', `Calque ${planLayers.length + 1}`)
            if (name) setPlanLayer(name)
          },
          onToggleLayerVisibility: (name) => setHiddenPlanLayers((h) =>
            h.includes(name) ? h.filter((x) => x !== name) : [...h, name]),
          planColor,
          onSetPlanColor: setPlanColor,
          planStyle,
          onSetPlanStyle: setPlanStyle,
          mySymbols,
          planSymbol,
          onSetPlanSymbol: setPlanSymbol,
          onUndoFeature: () => {
            if (planDraft.length) { setPlanDraft((d) => d.slice(0, -1)); return }
            setActivePlan((p) => p && { ...p, features: p.features.slice(0, -1) })
            setPlanDirty(true)
          },
          onSavePlan: handleSavePlan,
        }}
        constructionMode={constructionMode}
        onToggleConstructionMode={handleToggleConstructionMode}
        structureTypeData={structureTypeData}
        housingOptions={housingOptions}
        constructionSelection={constructionSelection}
        onConstructionSelectionChange={handleConstructionSelectionChange}
        constructionStatus={constructionStatus}
        skipConstructionConfirm={skipConstructionConfirm}
        onToggleSkipConstructionConfirm={() => setSkipConstructionConfirm((v) => !v)}
        notifySecret={notifySecret}
        onToggleNotifySecret={handleToggleNotifySecret}
        hasSecretChannel={!!myProfile?.has_secret_channel}
        constructionTool={constructionTool}
        onConstructionToolChange={handleConstructionToolChange}
        infrastructurePath={infrastructurePath}
        infraTypes={infraTypes}
        infrastructureSelection={infrastructureSelection}
        onInfrastructureSelectionChange={(field, value) => setInfrastructureSelection((prev) => ({ ...prev, [field]: value }))}
        onFinishInfrastructure={handleFinishInfrastructure}
        onRestartInfrastructure={handleRestartInfrastructure}
        onUndoInfraPoint={handleUndoInfraPoint}
        onCancelInfrastructure={handleCancelInfrastructure}
        editingInfra={editingInfra}
      />

      <MapView
        layers={mapLayers}
        filters={filters}
        planMode={planMode}
        customColors={customColorsFlat}
        selectionMode={selectionMode}
        isGlobeView={isGlobeView}
        satelliteMode={satelliteMode}
        borderMode={borderMode}
        borderWidth={borderWidth}
        borderColor={borderColor}
        borderOpacity={borderOpacity}
        borderStyle={borderStyle}
        haloCountryIds={haloEnabled ? [selectedRegion?.country_id, ownedCountryId].filter((v) => v != null) : []}
        haloColor={haloColor}
        mapBackground={mapBackground}
        rulerMode={rulerMode}
        rulerPath={rulerPath}
        onRulerClick={handleRulerClick}
        onRulerPointMove={handleRulerPointMove}
        onRulerPointDelete={handleRulerPointDelete}
        organizationsCatalog={organizationsCatalog}
        selectedOrgId={selectedOrgId}
        showToast={showToast}
        onMapLoaded={() => setMapLoaded(true)}
        onRegionClick={(r) => { if (mapState().milPlanToolActive) return; setSelectedRegion(r) }}
        onRegionPaint={handleRegionPaint}
        onRegionErase={handleRegionErase}
        selectedRegion={selectedRegion}
        onCenterToRegion={handleSetCenterRegionHandler}
        onRegionsDataLoaded={handleRegionsDataLoaded}
        regionStats={regionStats}
        statType={statType}
        structFilter={structFilter}
        weatherData={displayWeather}
        showStructures={showStructures}
        showInfrastructure={showInfrastructure}
        showCities={showCities}
        showRoads={showRoads}
        showRailways={showRailways}
        showBubbles={showBubbles}
        bubbleMetric={bubbleMetric}
        bubbleColor={bubbleColor}
        bubbleSize={bubbleSize}
        overlayStyles={overlayStyles}
        structuresData={structuresData}
        infrastructureData={infrastructureData}
        constructionMode={structureToolActive}
        onStructurePlace={handleStructurePlace}
        onStructureMove={handleStructureMove}
        infrastructureMode={infraToolActive}
        infrastructurePath={infrastructurePath}
        onInfrastructureClick={handleInfrastructureClick}
        onInfraPointMove={handleInfraPointMove}
        onInfraPointDelete={handleInfraPointDelete}
        onInfraPointFork={handleInfraPointFork}
        onInfraEditStart={handleInfraEditStart}
        showMilitary={showMilitary}
        militaryUnits={visibleMilitaryUnits}
        onUnitClick={setSelectedUnit}
        onUnitMoved={handleUnitMoved}
        onUnitDropped={handleUnitDropped}
        milPlanFeatures={milPlanFeatures}
        milPlanActive={!!(activePlan && !readOnlyPlan && planTool)}
        onMilPlanClick={handlePlanClick}
        onOpenSettings={() => setSettingsOpen(true)}
      />

      {selectedUnit && (
        <UnitPanel unit={selectedUnit} onClose={() => setSelectedUnit(null)} />
      )}

      {selectedRegion && !planMode && (
        <RegionPopup
          region={selectedRegion}
          onClose={() => setSelectedRegion(null)}
          onCenterMap={handleCenterMap}
          onShareLocation={handleShareLocation}
          stats={regionStats?.[selectedRegion.region_id] ?? regionStats?.[String(selectedRegion.region_id)]}
          weather={displayWeather?.regions?.[selectedRegion.region_id] ?? displayWeather?.regions?.[String(selectedRegion.region_id)]}
          currentMonth={currentMonth}
        />
      )}
      
      {mapLoaded && !planMode && (
        <Legend
          layers={mapLayers}
          regionsData={regionsData}
          statType={statType}
          structFilter={structFilter}
          selectedOrgId={selectedOrgId}
          organizationsCatalog={organizationsCatalog}
        />
      )}

      {pendingConstruction && (
        <ConstructionConfirmModal
          region={pendingConstruction.region}
          constructionSelection={pendingConstruction.selection}
          housingOptions={housingOptions}
          cost={pendingConstruction.cost}
          lngLat={pendingConstruction.lngLat}
          onConfirm={handleConfirmConstruction}
          onCancel={handleCancelConstruction}
        />
      )}
    </div>
  )
}

export default App
